import { useMutation } from '@apollo/client';
import { emptyTranslation, translateBackend } from '@assets/i18n/config';
import { graphql } from '@generated/gql';
import {
  FeatureFlagTypes,
  SubAccountInactiveReasons,
} from '@generated/graphql';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { NetWorthWorkflowContext } from '@ovComponents/4-module/netWorthWorkflow/netWorthWorkflowContext';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useOrganizationFeatureFlag } from '@providers/userContextProvider';
import { Box, Typography } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AddNetWorthAutomaticallyModal from './netWorthAutomaticLink/addNetWorthAutomaticallyModal';
import AddNetWorthManuallyModal from './netWorthManualLink/addNetWorthManuallyModal';

const CLOSE_ACCOUNT = graphql(`
  mutation closeAccount($input: CloseAccountInput!) {
    closeAccount(input: $input) {
      account {
        id
      }
    }
  }
`);

export const NetWorthAdd = ({
  options,
  onNext,
  workflowCompletion,
  subStepId,
}: {
  options: any;
  onNext: () => void;
  workflowCompletion: any;
  subStepId: string;
}) => {
  const { netWorthData, setNetWorthData } = useContext(NetWorthWorkflowContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const automaticNetWorthLinkingEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.AutomaticNetWorthLinking,
  );
  const manualNetWorthLinkingEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ManualNetWorthLinking,
  );

  const applicableData =
    Object.keys(netWorthData).length !== 0 ? netWorthData : workflowData;
  const setApplicationContext =
    Object.keys(netWorthData).length !== 0 ? setNetWorthData : setWorkflowData;

  const [openAutomatically, setOpenAutomatically] = useState(false);
  const [openManually, setOpenManually] = useState(false);
  const [step, setStep] = useState(1);

  const [closeAccount] = useMutation(CLOSE_ACCOUNT);

  useEffect(() => {
    setOpenAutomatically(
      applicableData.netWorthData?.netWorthLinking === 'AUTOMATIC'
        ? true
        : false,
    );
    setOpenManually(
      applicableData.netWorthData?.netWorthLinking === 'MANUAL' ? true : false,
    );
    setStep(applicableData.netWorthData?.netWorthStep ?? 1);
  }, [
    applicableData.netWorthData?.netWorthLinking,
    applicableData.netWorthData?.netWorthStep,
  ]);

  const updateContext = (value: any) => {
    const changedNetWorthLinking =
      applicableData.netWorthData?.netWorthLinking !== value;
    if (changedNetWorthLinking) {
      setApplicationContext({
        ...applicableData,
        currentSubAccountId: '',
        currentAccountId: '',
        netWorthData: {
          ...applicableData.netWorthData,
          netWorthStep: 1,
          netWorthLinking: value,
          netWorthAccountNickname: emptyTranslation,
          externalSourceType: '',
          netWorthAmountCents: 0,
          publicToken: '',
          netWorthLinkingActionType: undefined,
          netWorthLinkingNotificationGroup: '',
          netWorthCurrency: undefined,
        },
      });
    }

    if (applicableData.currentSubAccountId && applicableData.currentAccountId) {
      closeAccount({
        variables: {
          input: {
            accountId: applicableData.currentAccountId,
            inactiveReason: SubAccountInactiveReasons.Other,
          },
        },
      });
    }
  };
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mb: 3.5,
        }}
      >
        <Typography variant='displayLarge' sx={{ fontWeight: 400 }}>
          {translateBackend(options.title)}
        </Typography>
        {options?.subTitle && (
          <Typography variant='bodyLarge' sx={{ fontWeight: 400 }}>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options?.subTitle)}
            </ReactMarkdown>
          </Typography>
        )}
      </Box>
      {options.enableAutomaticLink && automaticNetWorthLinkingEnabled && (
        <SelectionTile
          value='AUTOMATIC'
          options={[
            {
              label: translateBackend(options.automaticLinkTitle),
              subtitle: translateBackend(options.automaticLinkSubtitle),
              value: 'AUTOMATIC',
            },
          ]}
          type='completion'
          onChange={(e) => {
            if (workflowCompletion) {
              setOpenAutomatically(true);
              updateContext(e.target.value);
            }
          }}
        />
      )}
      {options.enableManualLink && manualNetWorthLinkingEnabled && (
        <SelectionTile
          value='MANUAL'
          options={[
            {
              label: translateBackend(options.manualLinkTitle),
              subtitle: translateBackend(options.manualLinkSubtitle),
              value: 'MANUAL',
            },
          ]}
          type='completion'
          onChange={(e) => {
            if (workflowCompletion) {
              setOpenManually(true);
              updateContext(e.target.value);
            }
          }}
        />
      )}
      {openManually && (
        <AddNetWorthManuallyModal
          open={openManually}
          onClose={() => setOpenManually(false)}
          step={step}
          setStep={setStep}
          onNext={onNext}
        />
      )}
      {openAutomatically && (
        <AddNetWorthAutomaticallyModal
          open={openAutomatically}
          onClose={() => setOpenAutomatically(false)}
          step={step}
          setStep={setStep}
          onNext={onNext}
          workflowCompletion={workflowCompletion}
          subStepId={subStepId}
        />
      )}
    </>
  );
};
